import { useState } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { Trash2, Upload, Loader2 } from "lucide-react";
import axios from "axios";
import { API_URL } from "./constants";

// ======================== FILE PAGE EDITOR ========================
export function FilePageEditor({ content, updateContent }) {
  const [uploading, setUploading] = useState(false);
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API_URL}/uploads?entity_type=templates&tag=file-attachment`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      updateContent("file_url", res.data.url);
      if (!content.file_name) updateContent("file_name", res.data.original_name);
      toast.success("File uploaded!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
    e.target.value = "";
  };

  return (
    <div className="space-y-3">
      <div className="space-y-1"><Label className="text-xs">File Name</Label><Input value={content.file_name || ""} onChange={e => updateContent("file_name", e.target.value)} className="h-9 text-sm" placeholder="Shingle Brochure.pdf" /></div>
      <div className="space-y-1">
        <Label className="text-xs">File</Label>
        <div className="flex gap-2">
          <Input value={content.file_url || ""} onChange={e => updateContent("file_url", e.target.value)} className="h-9 text-sm flex-1" placeholder="File URL or upload below" />
          <label className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer text-xs font-medium text-slate-600 transition-colors whitespace-nowrap" data-testid="file-page-upload">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {uploading ? "Uploading..." : "Upload File"}
            <input type="file" accept="image/*,.pdf" className="hidden" onChange={handleFileUpload} disabled={uploading} />
          </label>
        </div>
      </div>
      <div className="space-y-1"><Label className="text-xs">Description</Label><Input value={content.description || ""} onChange={e => updateContent("description", e.target.value)} className="h-9 text-sm" placeholder="Product brochure and color swatches" /></div>
    </div>
  );
}

// ======================== COVER PAGE EDITOR ========================
export function CoverPageEditor({ content, updateContent }) {
  const [uploading, setUploading] = useState(false);
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API_URL}/uploads?entity_type=templates&tag=cover-logo`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      updateContent("logo_url", res.data.url);
      toast.success("Logo uploaded!");
    } catch (err) {
      toast.error("Upload failed");
    }
    setUploading(false);
    e.target.value = "";
  };
  const handleBgImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API_URL}/uploads?entity_type=templates&tag=cover-bg`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      updateContent("bg_image_url", res.data.url);
      toast.success("Background image uploaded!");
    } catch (err) {
      toast.error("Upload failed");
    }
    setUploading(false);
    e.target.value = "";
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1"><Label className="text-xs">Headline</Label><Input value={content.headline || ""} onChange={e => updateContent("headline", e.target.value)} className="h-9 text-sm" placeholder="PROPOSAL" /></div>
        <div className="space-y-1"><Label className="text-xs">Subheadline</Label><Input value={content.subheadline || ""} onChange={e => updateContent("subheadline", e.target.value)} className="h-9 text-sm" placeholder="Prepared for:" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Company Logo</Label>
          <div className="flex gap-2 items-center">
            {content.logo_url ? (
              <div className="relative">
                <img src={content.logo_url} alt="Logo" className="h-10 rounded border border-slate-200 object-contain" />
                <button type="button" className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center" onClick={() => updateContent("logo_url", "")}>
                  <Trash2 className="w-2.5 h-2.5" />
                </button>
              </div>
            ) : null}
            <label className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer text-xs font-medium text-slate-600 transition-colors" data-testid="cover-logo-upload">
              {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
              {content.logo_url ? "Change" : "Upload Logo"}
              <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} disabled={uploading} />
            </label>
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Background Image (optional)</Label>
          <div className="flex gap-2 items-center">
            {content.bg_image_url ? (
              <div className="relative">
                <img src={content.bg_image_url} alt="BG" className="h-10 rounded border border-slate-200 object-cover w-16" />
                <button type="button" className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center" onClick={() => updateContent("bg_image_url", "")}>
                  <Trash2 className="w-2.5 h-2.5" />
                </button>
              </div>
            ) : null}
            <label className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer text-xs font-medium text-slate-600 transition-colors" data-testid="cover-bg-upload">
              {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
              {content.bg_image_url ? "Change" : "Upload BG"}
              <input type="file" accept="image/*" className="hidden" onChange={handleBgImageUpload} disabled={uploading} />
            </label>
          </div>
        </div>
      </div>
      <div className="space-y-1"><Label className="text-xs">Background Color</Label><Input type="color" value={content.bgColor || "#0F4C81"} onChange={e => updateContent("bgColor", e.target.value)} className="h-9 w-24" /></div>
      <div className="flex flex-wrap gap-4">
        {[["show_logo","Logo"],["show_date","Date"],["show_quote_number","Quote #"],["show_contact_info","Contact Info"],["show_company_info","Company Info"]].map(([k,l])=>(
          <label key={k} className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={content[k]!==false} onChange={e=>updateContent(k,e.target.checked)} className="rounded" />{l}</label>
        ))}
      </div>
    </div>
  );
}

// ======================== GALLERY PAGE EDITOR ========================
export function GalleryPageEditor({ content, updateContent }) {
  const [uploading, setUploading] = useState(false);
  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const newUrls = [];
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        const res = await axios.post(`${API_URL}/uploads?entity_type=templates&tag=gallery-image`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newUrls.push(res.data.url);
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    if (newUrls.length > 0) {
      const existing = (content.image_urls || "").trim();
      const updated = existing ? `${existing}\n${newUrls.join("\n")}` : newUrls.join("\n");
      updateContent("image_urls", updated);
      toast.success(`${newUrls.length} photo${newUrls.length > 1 ? "s" : ""} uploaded!`);
    }
    setUploading(false);
    e.target.value = "";
  };

  const imageList = (content.image_urls || "").split("\n").map(u => u.trim()).filter(Boolean);

  return (
    <div className="space-y-3">
      <div className="space-y-1"><Label className="text-xs">Gallery Title</Label><Input value={content.title || ""} onChange={e => updateContent("title", e.target.value)} className="h-9 text-sm" placeholder="Past Projects" /></div>
      <div className="flex items-center justify-between">
        <Label className="text-xs">Photos ({imageList.length})</Label>
        <div className="flex gap-2">
          <label className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer text-xs font-medium text-slate-600 transition-colors" data-testid="gallery-upload-btn">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {uploading ? "Uploading..." : "Upload Photos"}
            <input type="file" accept="image/*" multiple className="hidden" onChange={handleGalleryUpload} disabled={uploading} />
          </label>
          <Select value={content.layout || "grid"} onValueChange={v => updateContent("layout", v)}>
            <SelectTrigger className="h-8 w-28 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="grid">Grid</SelectItem>
              <SelectItem value="carousel">Carousel</SelectItem>
              <SelectItem value="masonry">Masonry</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {imageList.length > 0 && (
        <div className="grid grid-cols-4 gap-2">
          {imageList.map((url, idx) => (
            <div key={idx} className="relative group/img rounded-md overflow-hidden border border-slate-200 aspect-square bg-slate-50">
              <img src={url} alt={`Gallery ${idx + 1}`} className="w-full h-full object-cover" />
              <button
                type="button"
                className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover/img:opacity-100 transition-opacity"
                onClick={() => {
                  const updated = imageList.filter((_, i) => i !== idx).join("\n");
                  updateContent("image_urls", updated);
                }}
                data-testid={`gallery-remove-${idx}`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="space-y-1">
        <Label className="text-xs text-slate-400">Or paste image URLs (one per line)</Label>
        <Textarea value={content.image_urls || ""} onChange={e => updateContent("image_urls", e.target.value)} rows={3} className="text-xs font-mono" placeholder="https://example.com/photo1.jpg" />
      </div>
    </div>
  );
}
